<template>
  <div
    :class="$style.container"
    data-test-id="SubscriptionTypeEdit__container"
  >
    <RadioGroup
      v-model="subscriptionType"
      :inline="false"
      label="Billing Options"
      name="SubscriptionType"
      :options="$options.SubscriptionTypeOptions"
      required
      :showValidationMessage="false"
    />
    <div v-if="isSubscriptionAnnual">
      <InputLabel
        required
        text="User Count"
      />
      <TooltipIcon
        icon="info"
        :text="infoIconText"
      />
    </div>
    <div v-else>
      <ParagraphBody
        bodySize="small"
        weight="normal"
      >
        You will be billed on the first of each month based on your monthly
        <Link
          href="https://jumpcloud.com/legal"
          isExternal
          target="_blank"
          text="high water mark."
        />
      </ParagraphBody>
    </div>
    <InputNumberInline
      v-if="isSubscriptionAnnual"
      v-model="userCount"
      :class="$style.userCount"
      name="User Count"
      required
      rules="min_value:11"
      textAfter=" users"
    />
    <div>
      <InputLabel
        required
        text="Terms and Conditions"
      />
      <Checkbox
        :data-test-id="$testId('AgreeWithServiceAgreement')"
        :isMarkdown="true"
        label="I agree to the [JumpCloud Directory as a Service Agreement](https://jumpcloud.com/legal)"
        :value="serviceAgreementAccepted"
        @input="setServiceAgreementAccepted"
      />
    </div>
    <div :class="$style.buttonBar">
      <Button
        v-if="hasReachedSection"
        :class="$style.cancelButton"
        data-test-id="SubscriptionTypeEdit__cancel"
        hasNoBackground
        text="Cancel"
        type="primary"
        @click="handleCancel"
      />
      <Button
        data-test-id="SubscriptionTypeEdit__continue"
        hasOutline
        :isDisabled="!isContinueEnabled"
        text="Continue"
        type="secondary"
        @click="handleContinue"
      />
    </div>
  </div>
</template>

<script>
import {
  Button,
  Checkbox,
  InputLabel,
  InputNumberInline,
  Link,
  ParagraphBody,
  RadioGroup,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters } from 'vuex';

const pricingModule = 'PricingModule';

const annual = 'annual';
const monthly = 'monthly';

const SubscriptionTypeOptions = [
  {
    label: 'Annual Billing - **Save More**',
    value: annual,
    isMarkdown: true,
  },
  {
    label: 'Monthly Billing',
    value: monthly,
  },
];

export default {
  name: 'SubscriptionTypeEdit',

  SubscriptionTypeOptions,

  components: {
    Button,
    InputLabel,
    InputNumberInline,
    Link,
    ParagraphBody,
    RadioGroup,
    TooltipIcon,
    Checkbox,
  },

  props: {
    hasReachedSection: Boolean,
  },

  data() {
    return {
      infoIconText: `Your user count should be the minimum amount of users you
        expect to have in your directory throughout the contract period (1 year).
        We use this number to calculate your annual charges. Additional users
        beyond this number will be billed at the annual per user rate on a separate
        invoice on a monthly basis.`,
      userCountInput: '',
    };
  },

  computed: {
    ...mapGetters(pricingModule, {
      isAnnual: 'getIsAnnual',
      serviceAgreementAccepted: 'getServiceAgreementAccepted',
      storedUserCount: 'getUserCount',
    }),

    isContinueEnabled() {
      return (this.userCount > 10 || this.subscriptionType === monthly)
        && this.serviceAgreementAccepted;
    },

    isSubscriptionAnnual() {
      return this.subscriptionType === annual;
    },

    storedSubscriptionType() {
      return this.isAnnual ? annual : monthly;
    },

    subscriptionType: {
      get() {
        return this.isAnnual ? annual : monthly;
      },
      set(value) {
        this.setIsAnnual(value === annual);
      },
    },

    userCount: {
      get() {
        return this.userCountInput;
      },
      set(value) {
        this.userCountInput = value;
        this.setUserCount(parseInt(value, 10));
      },
    },
  },

  mounted() {
    this.userCountInput = this.storedUserCount;
  },

  methods: {
    ...mapActions(pricingModule, [
      'setIsAnnual',
      'setServiceAgreementAccepted',
      'setUserCount',
      'setPreviousToItem',
      'resetItemToPrevious',
    ]),

    handleCancel() {
      this.resetItemToPrevious();
      this.$emit('cancelled');
    },

    handleContinue() {
      this.setPreviousToItem();
      this.$emit('continue');
    },
  },
};
</script>

<style module scoped>
.container {
  display: flex;
  flex-direction: column;
}

.buttonBar {
  display: flex;
  justify-content: flex-end;
}

.cancelButton {
  margin-right: var(--jc-spacer-large);
}

.userCount {
  --inline-input-width: 10ch;
}
</style>
